import React from 'react';
import { Box, Container, Tooltip, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  GraphicEq as VoiceIcon,
  DescriptionOutlined as DocumentIcon,
  InsertChartOutlined as DataIcon,
  Router as RouterIcon,
  TouchApp as ButtonIcon,
  CloudOutlined as CloudIcon,
  MapOutlined as MapIcon,
  CreditCardOutlined as PaymentIcon,
  AutoAwesome as AiIcon,
} from '@mui/icons-material';
import StoreBadges from '../common/StoreBadges';

/**
 * Partner- und Technologieleiste am Seitenende der Landing Page.
 *
 * Bewusst als einheitliche, monochrome Wortmarken umgesetzt (Glyph + Name in
 * gleicher Typografie, gedimmt wie ein Wasserzeichen) statt mit den
 * Original-Logos der Partner: das hält die Leiste optisch ruhig, funktioniert
 * in jeder White-Label-Farbwelt und vermeidet die Nutzung fremder
 * Bildmarken ohne Freigabe. Sollen echte Logos gezeigt werden, genügt es,
 * `mark` durch ein <Box component="img" .../> zu ersetzen.
 */
const PARTNERS = [
  { name: 'fonio.ai', role: 'KI-Telefonie', mark: VoiceIcon },
  { name: 'DMRZ', role: 'Kassenabrechnung § 302 SGB V', mark: DocumentIcon },
  { name: 'opta data', role: 'Abrechnungszentrum / CareMan', mark: DataIcon },
  { name: 'AVM FRITZ!Box', role: 'Telefonie-Connector', mark: RouterIcon },
  { name: 'Taxi Butler', role: 'Bestellknopf für Hotel & Gastronomie', mark: ButtonIcon },
  { name: 'Google Firebase', role: 'Cloud-Infrastruktur in der EU', mark: CloudIcon },
  { name: 'Google Maps', role: 'Karten, Routen und Adressen', mark: MapIcon },
  // Apple App Store und Google Play stehen nicht als Wortmarke in dieser Liste —
  // sie werden unterhalb als offizielle Store-Badges gezeigt.
  { name: 'PayPal', role: 'Bezahlung direkt in der App', mark: PaymentIcon },
  { name: 'Mistral AI', role: 'Verordnungserkennung (EU)', mark: AiIcon },
];

const PartnerStrip = ({ appStoreUrl, playStoreUrl }) => {
  const theme = useTheme();

  return (
    <Box
      component="section"
      sx={{
        py: { xs: 7, md: 9 },
        px: 3,
        bgcolor: '#111111',
        color: alpha('#ffffff', 0.85),
        borderTop: `1px solid ${alpha('#ffffff', 0.08)}`,
      }}
    >
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', mb: { xs: 5, md: 6 } }}>
          <Typography
            variant="overline"
            sx={{ color: theme.palette.primary.main, letterSpacing: 2, fontWeight: 700 }}
          >
            Partner &amp; Technologien
          </Typography>
          <Typography
            variant="h4"
            component="h2"
            sx={{ fontWeight: 700, color: '#fff', mt: 1, mb: 1.5 }}
          >
            Angebunden an das, womit Sie ohnehin arbeiten
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: alpha('#ffffff', 0.6), maxWidth: 680, mx: 'auto' }}
          >
            Abrechnung, Telefonanlage, Kartendienste und App-Stores greifen ineinander –
            damit Sie keine Daten zweimal erfassen.
          </Typography>
        </Box>

        {/* Flex statt Grid: bei 11 Kacheln bleibt die letzte Reihe sonst
            linksbündig stehen — zentriert wirkt die Leiste ruhiger. */}
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: { xs: 1.5, md: 2 },
          }}
        >
          {PARTNERS.map(({ name, role, mark: Mark }) => (
              <Tooltip title={role} arrow enterTouchDelay={0} key={name}>
                <Box
                  sx={{
                    flex: {
                      xs: '1 1 calc(50% - 12px)',
                      sm: '1 1 calc(33.333% - 16px)',
                      md: '0 1 calc(25% - 16px)',
                      lg: '0 1 calc(16.666% - 17px)',
                    },
                    py: 2.5,
                    px: 1.5,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    gap: 1,
                    borderRadius: 3,
                    border: `1px solid ${alpha('#ffffff', 0.07)}`,
                    color: alpha('#ffffff', 0.55),
                    filter: 'grayscale(1)',
                    transition: 'color .25s ease, border-color .25s ease, transform .25s ease',
                    '&:hover': {
                      color: theme.palette.primary.main,
                      borderColor: alpha(theme.palette.primary.main, 0.4),
                      transform: 'translateY(-3px)',
                      filter: 'none',
                    },
                  }}
                >
                  <Mark sx={{ fontSize: 30, color: 'inherit' }} />
                  <Typography
                    sx={{
                      fontSize: '0.82rem',
                      fontWeight: 600,
                      letterSpacing: 0.6,
                      textTransform: 'uppercase',
                      color: 'inherit',
                      lineHeight: 1.3,
                    }}
                  >
                    {name}
                  </Typography>
                </Box>
              </Tooltip>
          ))}
        </Box>

        {/* Store-Badges statt Wortmarken — die Originale sind die etablierte
            Bildsprache und werden von Apple/Google genau so erwartet.
            Wichtig: In den Stores liegt die gebrandete App des Taxiunternehmens,
            nicht „die NextCab-App“ — deshalb die Erläuterung darunter. */}
        <Box sx={{ mt: { xs: 4, md: 5 } }}>
          <StoreBadges
            appStoreUrl={appStoreUrl}
            playStoreUrl={playStoreUrl}
            height={46}
            justify="center"
          />
          <Typography
            variant="body2"
            sx={{
              display: 'block',
              textAlign: 'center',
              mt: 2,
              mx: 'auto',
              maxWidth: 620,
              color: alpha('#ffffff', 0.55),
              lineHeight: 1.7,
            }}
          >
            In den Stores landet <strong>Ihre</strong> App — mit Ihrem Namen, Ihrem Logo und
            Ihrer Support-Adresse. Die Veröffentlichung übernehmen wir; ein eigenes
            Entwicklerkonto brauchen Sie dafür nicht.
          </Typography>
        </Box>

        <Typography
          variant="caption"
          sx={{ display: 'block', textAlign: 'center', mt: 4, color: alpha('#ffffff', 0.35) }}
        >
          Alle genannten Marken gehören ihren jeweiligen Inhabern und dienen hier
          ausschließlich der Beschreibung der jeweiligen Schnittstelle.
        </Typography>
      </Container>
    </Box>
  );
};

export default PartnerStrip;
